import { useEffect, useState } from 'react';

import { fetchQuery, graphql } from 'react-relay';

import { useRovoWorkspace } from '@townsquare/collab-context';
import { getAggRelayEnvironment } from '@townsquare/relay-utils/agg-environment';

import { useUserHasAgentCapabilitiesQuery } from './__generated__/useUserHasAgentCapabilitiesQuery.graphql';

type UserHasAgentCreateCapabilityProps = { skipFetch?: boolean };

/**
 * @param skipFetch skip the API call when this is true for feature gating purposes
 * @returns if the user has permissions to create agents or not - null if still loading
 */
export const useUserHasAgentCreateCapability = ({
	skipFetch,
}: UserHasAgentCreateCapabilityProps): boolean | null => {
	const rovoWorkspaceCloudId = useRovoWorkspace();
	const [isAbleToCreateAgents, setIsAbleToCreateAgents] = useState<boolean | null>(null);

	useEffect(() => {
		const fetchCreatePerms = async () => {
			if (rovoWorkspaceCloudId && !skipFetch) {
				const data = await fetchQuery<useUserHasAgentCapabilitiesQuery>(
					getAggRelayEnvironment(),
					graphql`
						query useUserHasAgentCapabilitiesQuery($cloudId: ID!) {
							atlassianStudio_userSiteContext(cloudId: $cloudId) {
								... on AtlassianStudioUserSiteContextOutput {
									userPermissions {
										isAbleToCreateAgents
									}
								}
							}
						}
					`,
					{
						cloudId: rovoWorkspaceCloudId,
					},
				).toPromise();

				if (data?.atlassianStudio_userSiteContext?.userPermissions) {
					const permissions = data?.atlassianStudio_userSiteContext?.userPermissions;
					setIsAbleToCreateAgents(permissions?.isAbleToCreateAgents);
				}
			}
		};

		fetchCreatePerms();
	}, [skipFetch, rovoWorkspaceCloudId]);

	return isAbleToCreateAgents;
};
